import { ChevronDown, ChevronUp, Grid, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getWorkCategories } from '../api/workApi'
import type { WorkCategoryInfo } from '../types/work'

export default function WorkCategories() {
  const navigate = useNavigate()
  const [categories, setCategories] = useState<WorkCategoryInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    getWorkCategories()
      .then(setCategories)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  function toggleCategory(id: string) {
    setExpandedId(prev => (prev === id ? null : id))
  }

  if (loading) {
    return (
      <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ height: '120px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', animation: 'pulse 1.5s ease-in-out infinite' }} />
          ))}
        </div>
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
      </div>
    )
  }

  // Collect all subcategories for price table
  const allSubs = categories.flatMap(c => c.subcategories.map(s => ({ ...s, categoryLabel: c.label })))

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Grid size={20} style={{ color: 'var(--primary, #6366f1)' }} />
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>AI Job Categories</h1>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted, #6b7280)' }}>
              {categories.length} categories · {allSubs.length} task types
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/work/create')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', background: 'var(--primary, #6366f1)', color: '#fff', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
        >
          <Plus size={14} />
          Create Job
        </button>
      </div>

      {/* Category cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
        {categories.map(cat => (
          <div
            key={cat.id}
            style={{
              borderRadius: '14px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              overflow: 'hidden',
            }}
          >
            {/* Card header */}
            <div
              onClick={() => toggleCategory(cat.id)}
              style={{
                padding: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '10px',
              }}
            >
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
                  {cat.label}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {cat.description}
                </div>
                <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-muted, #6b7280)' }}>
                  {cat.subcategories.length} task types
                </div>
              </div>
              <div style={{ flexShrink: 0, color: 'var(--text-muted, #6b7280)', marginTop: '2px' }}>
                {expandedId === cat.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>

            {/* Expanded subcategories */}
            {expandedId === cat.id && (
              <div style={{ borderTop: '1px solid var(--border-color)', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {cat.subcategories.map(sub => (
                  <div
                    key={sub.id}
                    style={{
                      padding: '10px 12px',
                      borderRadius: '10px',
                      background: 'rgba(99, 102, 241, 0.04)',
                      border: '1px solid rgba(99, 102, 241, 0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{sub.label}</span>
                      <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--primary, #6366f1)' }}>
                        {sub.pricing.amount} FLZ/{sub.pricing.unit.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{sub.description}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted, #6b7280)', fontStyle: 'italic' }}>{sub.example_use}</div>
                    <button
                      onClick={() => navigate(`/work/create?category=${cat.id}&subcategory=${sub.id}`)}
                      style={{ alignSelf: 'flex-end', marginTop: '4px', padding: '4px 10px', borderRadius: '6px', background: 'var(--primary, #6366f1)', color: '#fff', border: 'none', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Create Job →
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Price table */}
      <div>
        <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>
          Pricing Table
        </h2>
        <div style={{ borderRadius: '12px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(99, 102, 241, 0.06)' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Task Type</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI Model</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '11px', color: 'var(--text-muted, #6b7280)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Price</th>
              </tr>
            </thead>
            <tbody>
              {allSubs.map((sub, i) => (
                <tr
                  key={sub.id}
                  style={{ borderTop: i > 0 ? '1px solid var(--border-color)' : undefined }}
                >
                  <td style={{ padding: '10px 14px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500 }}>{sub.label}</td>
                  <td style={{ padding: '10px 14px', fontSize: '12px', color: 'var(--text-secondary)' }}>{sub.categoryLabel}</td>
                  <td style={{ padding: '10px 14px', fontSize: '11px', color: 'var(--text-muted, #6b7280)', fontFamily: 'monospace' }}>
                    {sub.model.split('/').pop()}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: 700, color: 'var(--primary, #6366f1)', textAlign: 'right' }}>
                    {sub.pricing.amount} FLZ/{sub.pricing.unit.replace(/_/g, ' ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
